import {
    GENX_FREE_MAX_SCRIPT_DURATION_SECONDS,
    GENX_PAID_MAX_SCRIPT_DURATION_SECONDS,
} from '../constants';
import { STAGE_IDS } from '../types';

import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import { RoleEnum } from 'js/src/client';
import type { FeatureLockModalContext } from 'js/src/data/modals';
import { LOCKED_FEATURES, MODAL_IDS, showModal } from 'js/src/data/modals';
import { COOKIE_KEYS, ImmutableFromJS } from 'js/src/libs/consts';
import Cookies from 'js-cookie';

/** How close (in days) to the cycle end the reset date becomes visible. */
export const RESET_DATE_VISIBLE_DAYS = 5;

/** Format a usage-cycle end date for billing copy, e.g. "May 18th, 2026". */
export const formatResetDate = (isoDate: string | undefined): string | null => {
    if (!isoDate) return null;
    try {
        return format(parseISO(isoDate), 'MMM do, yyyy');
    } catch {
        return null;
    }
};

/** Whether the cycle end is within RESET_DATE_VISIBLE_DAYS days from now. */
export const isNearResetDate = (isoDate: string | undefined): boolean => {
    if (!isoDate) return false;
    try {
        const days = differenceInCalendarDays(parseISO(isoDate), new Date());
        return days >= 0 && days <= RESET_DATE_VISIBLE_DAYS;
    } catch {
        return false;
    }
};

const ENTERPRISE_LOCK_MESSAGE =
    "It looks like you're on an enterprise plan and not on credit card billing. Please contact your customer success manager for billing support.";
const NON_ADMIN_LOCK_MESSAGE =
    "It looks like you don't have permission to make billing changes. Please contact your account Admin";

export const getBillingLockReason = (): string | null => {
    if (SERVER.USER.is_enterprise_plan) return ENTERPRISE_LOCK_MESSAGE;
    if (SERVER.USER.role !== RoleEnum.ADMIN) return NON_ADMIN_LOCK_MESSAGE;
    return null;
};

/**
 * Decide whether the first-visit upsell modal should fire. Triggers when the
 * user lands on the Generate (storyboard) stage with no existing animation
 * minutes and the show-once cookie unset. Used inside a `useEffect` whose side
 * effects (setting modal-visible state and writing the cookie) live at the
 * call site.
 */
export function shouldShowAnimationMinutesUpsell(input: {
    activeStageId: string;
    animationMinutesLimitPerCycle: number;
    trialAnimationSecondsLimit?: number;
}): boolean {
    if (input.activeStageId !== STAGE_IDS.GENERATE) return false;
    if (input.animationMinutesLimitPerCycle > 0) return false;
    if ((input.trialAnimationSecondsLimit ?? 0) > 0) return false;
    if (Cookies.get(COOKIE_KEYS.GENX_ANIMATION_MINUTES_UPSELL_SEEN) === '1') return false;
    return true;
}

/**
 * Whether the trial intro modal should be shown. Triggers on first "Animate"
 * click when trial minutes exist, no subscription, and the show-once cookie is unset.
 */
export function shouldShowTrialIntroModal(input: {
    trialAnimationSecondsLimit: number;
    hasSubscription: boolean;
}): boolean {
    if (input.hasSubscription) return false;
    if (input.trialAnimationSecondsLimit <= 0) return false;
    if (Cookies.get(COOKIE_KEYS.GENX_TRIAL_INTRO_SEEN) === '1') return false;
    return true;
}

/**
 * Whether the trial depleted modal should be shown. Triggers when the user
 * had trial minutes that are now fully consumed, has no subscription, and the
 * show-once cookie is unset. Users who were never granted a trial fall through
 * to the purchase modal instead.
 */
export function shouldShowTrialDepletedModal(input: {
    trialRemainingSeconds: number;
    isTrialUser: boolean;
    hasSubscription: boolean;
}): boolean {
    if (input.hasSubscription) return false;
    if (!input.isTrialUser) return false;
    if (input.trialRemainingSeconds > 0) return false;
    if (Cookies.get(COOKIE_KEYS.GENX_TRIAL_DEPLETED_SEEN) === '1') return false;
    return true;
}

/**
 * Whether the trial insufficient modal should be shown. Triggers when
 * trial balance is positive but not enough for the action, no subscription,
 * and the show-once cookie is unset.
 */
export function shouldShowTrialInsufficientModal(input: {
    trialRemainingSeconds: number;
    isTrialUser: boolean;
    hasSubscription: boolean;
}): boolean {
    if (input.hasSubscription) return false;
    if (!input.isTrialUser) return false;
    if (input.trialRemainingSeconds <= 0) return false;
    if (Cookies.get(COOKIE_KEYS.GENX_TRIAL_INSUFFICIENT_SEEN) === '1') return false;
    return true;
}

/**
 * Highest user id considered an "existing customer" for the animation-minutes
 * announcement. Accounts created after the feature launched (ids above this
 * cutoff) are new signups who never knew the old AI-media-credits model, so the
 * "introducing" announcement is irrelevant to them and is suppressed.
 */
export const ANIMATION_MINUTES_INTRO_MAX_USER_ID = 5416347;

/**
 * Whether the one-time "introducing animation minutes" announcement modal
 * should be shown on the dashboard. Triggers for existing paying users (account
 * predates the feature launch), once, until the show-once cookie is set. New
 * signups are excluded.
 */
export function shouldShowAnimationMinutesIntroModal(): boolean {
    if (SERVER.USER.plan_price <= 0) return false;
    if (SERVER.USER.id > ANIMATION_MINUTES_INTRO_MAX_USER_ID) return false;
    if (Cookies.get(COOKIE_KEYS.GENX_ANIMATION_MINUTES_INTRO_SEEN)) return false;
    return true;
}

/**
 * When a user is out of animation minutes, decide where to send them:
 * - `'addon'`: the user is on a paid plan, so offer the add-on purchase flow to top up.
 * - `'pricing'`: the user is on the free plan, so send them to the pricing page to
 *   move onto a plan that includes animation minutes.
 */
export function getOutOfMinutesUpsellTarget(): 'addon' | 'pricing' {
    return SERVER.USER.plan_price > 0 ? 'addon' : 'pricing';
}

/**
 * Build the action that opens the in-app upgrade modal for animation minutes. Mirrors the
 * AI-media upsell: the FeatureLockModal shows the lowest paid plan inline (with an in-app
 * upgrade button) so a free user can upgrade without leaving the editor, falling back to the
 * pricing page if the plan tier can't be resolved.
 */
export function buildAnimationMinutesUpsellModalAction() {
    return showModal({
        modal_id: MODAL_IDS.FEATURE_LOCK,
        modal_context: ImmutableFromJS<FeatureLockModalContext>({
            featureName: LOCKED_FEATURES.ANIMATION_MINUTES,
            subCategory: 'genx animation minutes',
        }),
    });
}

/**
 * The longest script (in seconds) the current account is allowed to generate or
 * paste for a GenX video. Server-computed: 30s for free accounts, higher for
 * paid. Authoritative source of truth, mirrored by the chatterway backend guard.
 */
export function getGenxMaxScriptDurationSeconds(): number {
    return (
        SERVER?.USER?.capabilities?.genx_max_script_duration_seconds ??
        GENX_FREE_MAX_SCRIPT_DURATION_SECONDS
    );
}

/**
 * The effective paste/generate script-duration limit (in seconds) for the current
 * account, combining the GenX script-duration cap with the account's max video
 * duration. The cap only binds *gated* (free-tier) accounts; paid accounts report
 * the paid ceiling, which is just the largest pickable target — not a paste limit —
 * so they fall back to the account max. This is the single source of truth shared by
 * the paste UI's over-limit messaging and the script stage's Continue gate, so the
 * red warning and the disabled Continue button always agree on the limit.
 */
export function getEffectiveScriptDurationLimitSeconds(
    scriptDurationCapSeconds: number | undefined,
    maxVideoDurationSeconds: number,
): number {
    const isCapActive =
        scriptDurationCapSeconds !== undefined &&
        scriptDurationCapSeconds < GENX_PAID_MAX_SCRIPT_DURATION_SECONDS;
    return isCapActive
        ? Math.min(scriptDurationCapSeconds, maxVideoDurationSeconds)
        : maxVideoDurationSeconds;
}

/**
 * Build the action that opens the in-app upgrade modal for the GenX script-duration
 * cap. Mirrors the animation-minutes upsell: the FeatureLockModal shows the lowest
 * paid plan inline so a free user can upgrade without leaving the editor.
 */
export function buildGenxScriptDurationUpsellModalAction() {
    return showModal({
        modal_id: MODAL_IDS.FEATURE_LOCK,
        modal_context: ImmutableFromJS<FeatureLockModalContext>({
            featureName: LOCKED_FEATURES.GENX_SCRIPT_DURATION,
            subCategory: 'genx script duration',
        }),
    });
}

/**
 * Format an animation-minute cost (in seconds) for billing copy.
 * Sub-minute costs omit the minute component (e.g. "4s", not "0 min 4s").
 * Examples: 4 → "4s", 45 → "45s", 60 → "1 min", 96 → "1 min 36s", 120 → "2 mins", 150 → "2 mins 30s".
 */
export const formatAnimationMinutesCost = (totalSeconds: number): string => {
    // Callers sometimes derive totalSeconds via (limit/60 - used/60) * 60,
    // which produces float-precision garbage like 577.0000000000001. Round
    // to a whole second so the display never shows ugly decimals.
    const wholeSeconds = Math.round(totalSeconds);
    if (wholeSeconds <= 0) return '0s';
    if (wholeSeconds < 60) return `${wholeSeconds}s`;
    const minutes = Math.floor(wholeSeconds / 60);
    const seconds = wholeSeconds % 60;
    const minutesLabel = `${minutes} ${minutes === 1 ? 'min' : 'mins'}`;
    return seconds === 0 ? minutesLabel : `${minutesLabel} ${seconds}s`;
};
